<template>
    <b-sidebar
      id="add-new-diagnostic-image-category-sidebar"
      :visible="isAddNewDiagnosticImageCategorySidebarActive"
      bg-variant="white"
      sidebar-class="sidebar-lg"
      shadow
      backdrop
      no-header
      right
      @hidden="resetForm"
      @change="
        val => $emit('update:is-add-new-diagnostic-image-category-sidebar-active', val)
      ">
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">{{ $t('Add') }} {{ $t('New') }} Package</h5>
  
          <i class="fas fa-times ml-1 cursor-pointer" @click="hide"></i>
        </div>
  
        <!-- BODY -->
        <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
          <!-- Form -->
          <b-form
            class="p-2"
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm">
            <!-- Full Name -->
            <validation-provider #default="validationContext" name="name" rules="required">
              <b-form-group :label="$t('Form.Name')" label-for="name">
                <b-form-input
                  id="name"
                  v-model="stateData.name"
                  autofocus
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Type package name" />
  
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <b-form-group label="Package type" label-for="package-kind">
              <treeselect
                id="package-kind"
                v-model="packageKind"
                :options="typeOptions"
                :normalizer="normalizer"
                :clearable="false"
                @input="onPackageKindInput" />
              <small class="text-muted d-block mt-1">
                IV serum includes the IV therapies marker treatment.
              </small>
              <small v-if="missingIvTreatment" class="text-danger d-block">
                Configure the IV therapies marker treatment before marking a package as IV serum.
              </small>
            </b-form-group>
  
            <!-- Price -->
            <validation-provider #default="validationContext" name="price" rules="required">
              <b-form-group :label="$t('Price')" label-for="price">
                <b-form-input
                  id="price"
                  v-model="stateData.price"
                  autofocus
                  type="number"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Type package price" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
  
            <!-- Price -->
            <validation-provider
              #default="validationContext"
              name="price cost"
              rules="required">
              <b-form-group :label="$t('Price Cost')" label-for="price">
                <b-form-input
                  id="price"
                  v-model="stateData.price_cost"
                  autofocus
                  type="number"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Type package price" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
  
            <!-- Diagnostic imagings -->
            <b-form-group label="Diagnostic imagings" label-for="Diagnostic imagings">
              <treeselect
                id="status"
                v-model="stateData.diagnostic_imagings"
                multiple
                :options="optionsDiagnostic"
                :normalizer="normalizer" />
            </b-form-group>
  
            <!-- Laboratories -->
            <b-form-group label="Laboratories" label-for="Laboratories">
              <treeselect
                id="status"
                v-model="stateData.laboratories"
                multiple
                :options="optionsLaboratories"
                :normalizer="normalizer" />
            </b-form-group>
  
            <!-- Procedures -->
            <b-form-group label="Procedures" label-for="Procedures">
              <treeselect
                id="status"
                v-model="stateData.procedures"
                multiple
                :options="optionsProcedures"
                :normalizer="normalizer" />
            </b-form-group>
  
            <!-- Test -->
            <b-form-group label="Test" label-for="Test">
              <treeselect
                id="status"
                v-model="stateData.tests"
                multiple
                :options="optionsTest"
                :normalizer="normalizer" />
            </b-form-group>
  
            <!-- Treatments -->
            <b-form-group label="Treatments" label-for="Treatments">
              <treeselect
                id="status"
                v-model="stateData.treatments"
                multiple
                :options="optionsTreatments"
                :normalizer="normalizer" />
            </b-form-group>
  
            <div v-for="(treat, index) in stateData.treatments" :key="index">
              <b-form-group
                :label="`Quantity of ${getTreatment(treat)}`"
                label-for="Treatments">
                <b-form-input v-model="stateData.quantity[treat]" type="number" />
              </b-form-group>
            </div>
  
            <b-form-group label="Description" label-for="description">
              <b-form-textarea
                id="description"
                v-model="stateData.description"
                rows="3"
                placeholder="Optional" />
            </b-form-group>

            <!-- status -->
            <validation-provider
              #default="validationContext"
              name="Status"
              rules="required">
              <b-form-group :label="$t('Form.Status')" label-for="status">
                <treeselect
                  id="status"
                  v-model="stateData.status"
                  :state="getValidationState(validationContext)"
                  :options="optionsStatus"
                  :normalizer="normalizer" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
  
            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button variant="primary" class="mr-2" type="submit" :disabled="loading">
                <span v-if="!loading">{{ $t('Add') }}</span>
                <span v-else>
                  <SpinnerLoading />
                </span>
              </b-button>
              <b-button type="button" variant="outline-danger" @click="hide">
                {{ $t('Cancel') }}
              </b-button>
            </div>
          </b-form>
        </validation-observer>
        <ToastNotification ref="toastRef" />
      </template>
    </b-sidebar>
  </template>
  
  <script>
  import {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormInvalidFeedback,
    BButton,
  } from 'bootstrap-vue'
  import formValidation from '@/core/utils/forms/form-validation'
  import ToastNotification from '@/components/ToastNotification'
  import { ValidationProvider, ValidationObserver } from 'vee-validate'
  import SpinnerLoading from '@/components/SpinnerLoading'
  
  import { required } from '@validations'
  import { ref, watch } from '@vue/composition-api'
  import Vue from 'vue'
  import axiosAC from '@/core/services/api/admin/consults/packages'
  import {
    findIvTherapiesTreatment,
    packageKindFromTreatments,
    syncTreatmentsForPackageKind,
  } from '@/core/utils/ivTherapies'
  
  export default {
    components: {
      BSidebar,
      BForm,
      BFormGroup,
      BFormInput,
      BFormTextarea,
      BFormInvalidFeedback,
      BButton,
      SpinnerLoading,
      ToastNotification,
      // Form Validation
      ValidationProvider,
      ValidationObserver,
    },
    model: {
      prop: 'isAddNewDiagnosticImageCategorySidebarActive',
      event: 'update:is-add-new-diagnostic-image-category-sidebar-active',
    },
    props: {
      isAddNewDiagnosticImageCategorySidebarActive: {
        type: Boolean,
        required: true,
      },
      optionsDiagnostic: {
        type: Array,
        required: true,
      },
      optionsLaboratories: {
        type: Array,
        required: true,
      },
      optionsProcedures: {
        type: Array,
        required: true,
      },
      optionsTest: {
        type: Array,
        required: true,
      },
      optionsTreatments: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        required,
        normalizer(node) {
          return {
            id: node.id,
            label: node.name,
          }
        },
      }
    },
    setup(props, { emit }) {
      const optionsStatus = [
        { name: 'Active', id: true },
        { name: 'Inactive', id: false },
      ]
      const typeOptions = [
        { name: 'Normal package', id: 'normal' },
        { name: 'IV serum', id: 'iv' },
      ]
      const packageKind = ref('normal')
      const missingIvTreatment = ref(false)
      const getTreatment = treatment => {
        const trt = props.optionsTreatments.find(tr => tr.id == treatment)
        return trt ? trt.name : ''
      }
      const stateDataTem = {
        name: '',
        description: '',
        status: true,
        quantity: [],
        diagnostic_imagings: [],
        laboratories: [],
        procedures: [],
        tests: [],
        treatments: [],
      }
      const stateData = ref(JSON.parse(JSON.stringify(stateDataTem)))

      const applyPackageKind = kind => {
        const iv = findIvTherapiesTreatment(props.optionsTreatments)
        missingIvTreatment.value = kind === 'iv' && !iv
        const { ids, ivId } = syncTreatmentsForPackageKind(
          kind,
          stateData.value.treatments,
          iv
        )
        stateData.value.treatments = ids
        if (kind === 'iv' && ivId != null && !stateData.value.quantity[ivId]) {
          Vue.set(stateData.value.quantity, ivId, 1)
        }
      }
      const onPackageKindInput = value => {
        const kind = value || 'normal'
        packageKind.value = kind
        applyPackageKind(kind)
      }
  
      const resetuserData = () => {
        stateData.value = JSON.parse(JSON.stringify(stateDataTem))
        packageKind.value = 'normal'
        missingIvTreatment.value = false
      }
      const { refFormObserver, getValidationState, resetForm } =
        formValidation(resetuserData)
  
      const loading = ref(false)
      const toastRef = ref(null)
      const normalizeArrayField = value => (Array.isArray(value) ? value : [])

      const onSubmit = async () => {
        loading.value = true

        try {
          if (packageKind.value === 'iv' && findIvTherapiesTreatment(props.optionsTreatments) == null) {
            throw new Error('Configure the IV therapies marker treatment before saving an IV serum')
          }

          const selectedTreatments = normalizeArrayField(stateData.value.treatments)

          const newTreatments = processTreatments(
            stateData.value.quantity,
            selectedTreatments
          )

          const datos = {
            ...stateData.value,
            description:
              stateData.value.description != null ? stateData.value.description : '',
            diagnostic_imagings: normalizeArrayField(stateData.value.diagnostic_imagings),
            laboratories: normalizeArrayField(stateData.value.laboratories),
            procedures: normalizeArrayField(stateData.value.procedures),
            tests: normalizeArrayField(stateData.value.tests),
            treatments: newTreatments,
          }

          const response = await axiosAC.createPackage(datos)
          const registro = response?.data ?? response?.registro

          if (!registro || (response?.status && response.status !== 'OK')) {
            throw new Error(response?.message || 'Package could not be created')
          }

          resetuserData()
          emit('createDiagnosticImageCategory', { status: true, registro })
        } catch (error) {
          toastRef.value.danger(error.message || 'Package could not be created')
        } finally {
          loading.value = false
        }
      }
  
      const processTreatments = (quantities, treatments) => {
        if (!treatments.length) {
          return []
        }
  
        return treatments.map(treatmentId => {
          const quantity = quantities[treatmentId]
          if (!quantity || quantity <= 0) {
            throw new Error('Check all the treatments!')
          }
          return { id: treatmentId, cant: quantity }
        })
      }

      watch(
        () => stateData.value.treatments,
        ids => {
          const next = packageKindFromTreatments(
            ids,
            findIvTherapiesTreatment(props.optionsTreatments)
          )
          if (packageKind.value !== next) packageKind.value = next
        }
      )

      return {
        loading,
        stateData,
        optionsStatus,
        typeOptions,
        packageKind,
        missingIvTreatment,
        onPackageKindInput,
        onSubmit,
        refFormObserver,
        getValidationState,
        resetForm,
        getTreatment,
        toastRef,
      }
    },
  }
  </script>
  
  <style></style>
  